/* ==========================================================================
 * BUNDLE CART INTEGRITY — per bundle INSTANCE
 * --------------------------------------------------------------------------
 * A bundle is one Duvet + one Sheet held as two real cart lines (Cart
 * Transform merge needs Shopify Plus for custom apps). Nothing natively keeps
 * those two lines in step, so this does.
 *
 * IDENTITY MODEL — the important part
 *   _bundle_config  which CONFIGURATION (which duvet + which sheet)
 *   _bundle_id      which PARTICULAR bundle, unique per add
 *
 * Pairing is done on _bundle_id, never on _bundle_config. Two bundles can hold
 * identical contents; grouping on configuration would merge them and let an
 * action on one silently alter the other.
 *
 * BEHAVIOUR
 *   component removed / set to 0  ->  its partner is removed too
 *   component quantity changed    ->  its partner is set to the SAME quantity
 *
 * Deliberately NOT min(duvet, sheet): that collapses an intentional increase
 * back down. Instead the script snapshots quantities and, on the next pass,
 * syncs the partner to whichever line the customer actually changed.
 *
 * SAFETY
 *   - Lines without _bundle_id are ignored entirely; ordinary products and
 *     standalone Duvet/Sheet purchases are never touched.
 *   - Observes jQuery AJAX and fetch rather than editing cart-ajax.js, a
 *     shared file driving every cart interaction store-wide.
 *   - Re-entrancy guarded, and it never acts without a prior snapshot to
 *     compare against, so it cannot guess wrong on first load.
 * ========================================================================== */
(function () {
  'use strict';

  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var SNAPSHOT_KEY = 'xbBundleQtySnapshot';
  var busy = false;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function prop(line, key) {
    return line && line.properties ? line.properties[key] : undefined;
  }

  function loadSnapshot() {
    try { return JSON.parse(sessionStorage.getItem(SNAPSHOT_KEY) || '{}'); }
    catch (e) { return {}; }
  }

  function saveSnapshot(map) {
    try { sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify(map)); } catch (e) { /* private mode */ }
  }

  /* { bundleId: { duvet: line, sheet: line } } — only complete-ish groups. */
  function groupByInstance(cart) {
    var groups = {};
    (cart.items || []).forEach(function (line) {
      var id = prop(line, '_bundle_id');
      var role = prop(line, '_bundle_role');
      if (!id || (role !== 'duvet' && role !== 'sheet')) return;   // not a bundle line
      if (!groups[id]) groups[id] = {};
      /* A crafted cart could repeat a role; keep the first and ignore extras. */
      if (!groups[id][role]) groups[id][role] = line;
    });
    return groups;
  }

  /**
   * Decide what each bundle instance needs, comparing against the snapshot.
   * @returns {{updates: Object, snapshot: Object}}
   */
  function plan(cart, previous) {
    var groups = groupByInstance(cart);
    var updates = {};
    var snapshot = {};

    Object.keys(groups).forEach(function (bundleId) {
      var g = groups[bundleId];
      var duvet = g.duvet;
      var sheet = g.sheet;

      /* ---- one half is gone: remove the remaining half ---- */
      if (!duvet || !sheet) {
        var survivor = duvet || sheet;
        if (survivor) updates[survivor.key] = 0;
        return;   /* nothing to snapshot; the instance is being dissolved */
      }

      var prev = previous[bundleId];
      var dq = duvet.quantity;
      var sq = sheet.quantity;

      if (dq === sq) {
        snapshot[bundleId] = { q: dq, duvet: duvet.key, sheet: sheet.key };
        return;   /* already in step */
      }

      /* ---- quantities differ: sync the partner to the line that changed ---- */
      if (!prev) {
        /* No prior state to compare against (first load, or a cart carried in
           from another session). Guessing which side the customer meant could
           silently undo their edit, so leave it and snapshot what we see. */
        snapshot[bundleId] = { q: Math.max(dq, sq), duvet: duvet.key, sheet: sheet.key };
        return;
      }

      var duvetChanged = dq !== prev.q;
      var sheetChanged = sq !== prev.q;
      var target;

      if (duvetChanged && !sheetChanged) target = dq;
      else if (sheetChanged && !duvetChanged) target = sq;
      else target = Math.max(dq, sq);   /* both moved: honour the larger intent */

      if (target <= 0) {
        updates[duvet.key] = 0;
        updates[sheet.key] = 0;
        return;
      }

      if (dq !== target) updates[duvet.key] = target;
      if (sq !== target) updates[sheet.key] = target;
      snapshot[bundleId] = { q: target, duvet: duvet.key, sheet: sheet.key };
    });

    return { updates: updates, snapshot: snapshot };
  }

  function reconcile() {
    if (busy) return;
    busy = true;

    fetch(root() + 'cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var result = plan(cart, loadSnapshot());
        saveSnapshot(result.snapshot);

        var keys = Object.keys(result.updates);
        if (!keys.length) { busy = false; return null; }

        console.warn('[bundle-cart-integrity] syncing ' + keys.length +
          ' line(s) to keep bundle instances consistent');

        return fetch(root() + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ updates: result.updates })
        }).then(function (r) { return r.json(); });
      })
      .then(function (updated) {
        busy = false;
        if (!updated) return;
        refreshCartUi(updated);
        document.dispatchEvent(new CustomEvent('bundle:cart-synced', { detail: updated }));
      })
      .catch(function (err) {
        busy = false;
        console.error('[bundle-cart-integrity] reconcile failed', err);
      });
  }

  function refreshCartUi(cart) {
    if (typeof window.jQuery !== 'undefined' && typeof window.buildCart === 'function') {
      try { window.buildCart(cart, false); return; } catch (e) { /* fall through */ }
    }
    if (/\/cart/.test(window.location.pathname)) window.location.reload();
  }

  function start() {
    if (typeof window.jQuery !== 'undefined') {
      window.jQuery(document).ajaxComplete(function (event, xhr, settings) {
        if (settings && settings.url && CART_MUTATION.test(settings.url)) reconcile();
      });
    }

    if (window.fetch) {
      var nativeFetch = window.fetch;
      window.fetch = function (input) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var result = nativeFetch.apply(this, arguments);
        if (CART_MUTATION.test(url)) {
          result.then(function () { setTimeout(reconcile, 0); }).catch(function () {});
        }
        return result;
      };
    }

    reconcile();
  }

  /* Exposed for testing. */
  window.__bundleIntegrityPlan = plan;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
